import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, File, Image } from 'lucide-react';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { Button } from './ui/button';

const MAX_BYTES = 10 * 1024 * 1024;

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) return <Image size={20} strokeWidth={1.5} />;
  if (mimeType === 'application/pdf') return <FileText size={20} strokeWidth={1.5} />;
  return <File size={20} strokeWidth={1.5} />;
}

export function AttachmentsPanel({ transactionId }: { transactionId: number }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['attachments', transactionId],
    queryFn: () => api.listAttachments(transactionId),
  });
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadAttachment(transactionId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attachments', transactionId] });
      if (inputRef.current) inputRef.current.value = '';
    },
  });
  const del = useMutation({
    mutationFn: (id: number) => api.deleteAttachment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', transactionId] }),
  });

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      alert('File too large (max 10MB).');
      e.target.value = '';
      return;
    }
    upload.mutate(file);
  }

  return (
    <div className="mt-4 rounded-lg border border-border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium text-foreground">Attachments</div>
        <label
          className={`inline-flex h-8 cursor-pointer items-center gap-2 rounded-md border border-border bg-card px-3 text-xs font-medium text-foreground transition-colors hover:bg-card/80 focus-within:ring-2 focus-within:ring-primary ${upload.isPending ? 'pointer-events-none opacity-50' : ''}`}
        >
          {upload.isPending ? 'Uploading…' : '+ Upload'}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={onFileChange}
            disabled={upload.isPending}
            className="hidden"
          />
        </label>
      </div>
      {isLoading && <div className="text-xs text-muted-foreground/70">Loading…</div>}
      {data && data.attachments.length === 0 && (
        <div className="text-xs text-muted-foreground/70">No attachments yet.</div>
      )}
      {data && data.attachments.length > 0 && (
        <ul className="space-y-2">
          {data.attachments.map((a) => (
            <li key={a.id} className="flex items-center gap-3 rounded bg-background p-2 text-sm">
              {a.mime_type.startsWith('image/') ? (
                <img
                  src={`/api/attachments/${a.id}`}
                  alt={a.original_filename}
                  className="h-12 w-12 rounded object-cover transition-transform hover:scale-105"
                />
              ) : (
                <span className="flex h-12 w-12 items-center justify-center rounded bg-card text-muted-foreground">
                  <FileIcon mimeType={a.mime_type} />
                </span>
              )}
              <div className="flex-1">
                <div className="text-foreground">
                  <a
                    href={`/api/attachments/${a.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline"
                  >
                    {a.original_filename}
                  </a>
                </div>
                <div className="text-xs text-muted-foreground">
                  {fmtSize(a.size_bytes)} · {a.uploader_email} · {formatDateTime(a.created_at)}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm(`Delete ${a.original_filename}?`)) del.mutate(a.id);
                }}
                disabled={del.isPending}
                className="text-destructive hover:text-destructive"
              >
                Delete
              </Button>
            </li>
          ))}
        </ul>
      )}
      {upload.isError && (
        <div className="mt-2 text-xs text-destructive">
          Upload failed. Check size (≤10MB) and file type (JPEG/PNG/WebP/PDF).
        </div>
      )}
    </div>
  );
}
